<!--
  App.vue
  Root application component.
  Renders the top navigation bar and the router-view outlet.
  Nav includes brand, main links (Dashboard, Projects, Admin), and current user display.
-->
<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth.js'
import { clearApiCache } from '@/services/api.js'
import { TEST_ROLE_OPTIONS, getTestRoleOverride, getTestUserOverride, setTestRoleOverride, setTestUserOverride } from '@/services/auth.js'

const router = useRouter()
const route = useRoute()
const { user, isAdmin, isLoaded, loadUser } = useAuth()
const selectedTestRole = ref(getTestRoleOverride())
const selectedTestUser = ref(getTestUserOverride())
const isConnectingRoleCheck = ref(false)

/**
 * Fire-and-forget warmup call on app mount.
 * Establishes the SQL connection pool in the Azure Function runtime before the
 * user navigates anywhere, reducing first-page latency. Also suppresses
 * Azure SQL Serverless auto-pause while the app is in active use.
 */
onMounted(() => {
  fetch('/api/health', { credentials: 'include' }).catch(() => { /* warmup only */ })
})

/** Display name: prefer name, fall back to email prefix. */
const displayName = computed(() => {
  if (!user.value) return ''
  return user.value.name || user.value.email?.split('@')[0] || 'User'
})

/** Display role for smoke testing/access clarity. */
const displayRole = computed(() => {
  if (!user.value?.role) return ''
  return formatRole(user.value.role)
})

const roleSwitcherEnabled = computed(() => Boolean(user.value?.roleSwitcherEnabled))
const userSwitcherEnabled = computed(() => Boolean(user.value?.userSwitcherEnabled && user.value?.availableTestUsers?.length))
const availableTestUsers = computed(() => user.value?.availableTestUsers || [])
const showRoleCheckConnect = computed(() => {
  const source = user.value?.roleSource || ''
  return isLoaded.value
    && user.value
    && !roleSwitcherEnabled.value
    && ['prototype-default', 'strict-default', 'email-setting'].includes(source)
})

function formatRole(role) {
  return String(role || '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

async function reloadAfterTestSwitch() {
  clearApiCache()
  await loadUser()
  if (route.meta.requiresAdmin && selectedTestRole.value !== 'admin') {
    router.push('/dashboard')
  }
}

async function changeTestRole() {
  setTestRoleOverride(selectedTestRole.value)
  await reloadAfterTestSwitch()
}

async function changeTestUser() {
  setTestUserOverride(selectedTestUser.value)
  await reloadAfterTestSwitch()
}

async function connectRoleCheck() {
  isConnectingRoleCheck.value = true
  try {
    await loadUser({ allowInteractive: true, forceInteractive: true })
  } finally {
    isConnectingRoleCheck.value = false
  }
}

/** Whether any nav link is active. Used for aria-current. */
function isActive(path) {
  return route.path.startsWith(path)
}
</script>

<template>
  <div class="app-shell">
    <!-- ============================================================
         Top Navigation Bar
    ============================================================ -->
    <header class="nav-bar" role="banner">
      <div class="nav-inner">
        <!-- Brand -->
        <RouterLink to="/dashboard" class="nav-brand" aria-label="ReArch Project Executive Report home">
          <img src="/rearch-logo-primary.png" alt="ReArch Construction" class="nav-brand-logo" />
          <span class="nav-brand-copy">
            <span class="nav-brand-text">Project Executive Report</span>
            <span class="nav-brand-sub">Monthly Executive Reporting</span>
          </span>
        </RouterLink>

        <!-- Primary nav links -->
        <nav class="nav-links" aria-label="Primary navigation">
          <RouterLink
            to="/dashboard"
            class="nav-link"
            :class="{ 'nav-link--active': isActive('/dashboard') }"
            :aria-current="isActive('/dashboard') ? 'page' : undefined"
          >
            Dashboard
          </RouterLink>

          <RouterLink
            to="/projects"
            class="nav-link"
            :class="{ 'nav-link--active': isActive('/projects') }"
            :aria-current="isActive('/projects') ? 'page' : undefined"
          >
            Projects
          </RouterLink>

          <RouterLink
            v-if="isAdmin"
            to="/admin"
            class="nav-link"
            :class="{ 'nav-link--active': route.path === '/admin' }"
            :aria-current="route.path === '/admin' ? 'page' : undefined"
          >
            Admin
          </RouterLink>

          <RouterLink
            v-if="isAdmin"
            to="/admin/billing-forecast-lab"
            class="nav-link"
            :class="{ 'nav-link--active': isActive('/admin/billing-forecast-lab') }"
            :aria-current="isActive('/admin/billing-forecast-lab') ? 'page' : undefined"
          >
            Forecast Lab
          </RouterLink>
        </nav>

        <!-- User info -->
        <div class="nav-user" aria-label="Signed-in user">
          <template v-if="isLoaded && user">
            <label v-if="roleSwitcherEnabled" class="role-switcher">
              <span class="sr-only">Test role</span>
              <select v-model="selectedTestRole" class="role-switcher-select" @change="changeTestRole">
                <option value="">Actual role</option>
                <option v-for="opt in TEST_ROLE_OPTIONS" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </label>
            <label v-if="userSwitcherEnabled" class="role-switcher">
              <span class="sr-only">View as user</span>
              <select v-model="selectedTestUser" class="role-switcher-select" @change="changeTestUser">
                <option value="">Actual user</option>
                <option v-for="opt in availableTestUsers" :key="opt.email" :value="opt.email">
                  {{ opt.label || opt.email }}
                </option>
              </select>
            </label>
            <span class="nav-user-name">{{ displayName }}</span>
            <span v-if="displayRole" class="nav-user-role">{{ displayRole }}</span>
            <button
              v-if="showRoleCheckConnect"
              type="button"
              class="nav-role-connect"
              :disabled="isConnectingRoleCheck"
              title="Connect Microsoft Graph role check"
              @click="connectRoleCheck"
            >
              {{ isConnectingRoleCheck ? 'Checking...' : 'Check role' }}
            </button>
          </template>
          <span v-else-if="!isLoaded" class="nav-user-loading" aria-hidden="true">...</span>
        </div>
      </div>
    </header>

    <!-- ============================================================
         Page Content
    ============================================================ -->
    <main class="app-main" id="main-content">
      <RouterView />
    </main>
  </div>
</template>

<style scoped>
/* ---- Shell layout ---- */
.app-shell {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: var(--bg-page);
}

.app-main {
  flex: 1;
  padding: var(--space-md) var(--space-lg);
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

/* ---- Nav bar ---- */
.nav-bar {
  background: #fff;
  color: var(--text-primary);
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid var(--border);
  box-shadow: 0 1px 3px rgba(54, 87, 61, 0.12);
}

.nav-inner {
  display: flex;
  align-items: center;
  gap: var(--space-lg);
  padding: 0 var(--space-lg);
  height: 64px;
  max-width: 1400px;
  margin: 0 auto;
}

/* ---- Brand ---- */
.nav-brand {
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 12px;
  line-height: 1.2;
  color: var(--text-primary);
  flex-shrink: 0;
}

.nav-brand:hover {
  text-decoration: none;
}

.nav-brand-logo {
  width: 118px;
  height: auto;
  flex-shrink: 0;
}

.nav-brand-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: 12px;
  border-left: 1px solid var(--border);
}

.nav-brand-text {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--primary);
  letter-spacing: 0.01em;
}

.nav-brand-sub {
  font-size: 0.68rem;
  color: var(--text-secondary);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

/* ---- Nav links ---- */
.nav-links {
  display: flex;
  gap: var(--space-sm);
  flex: 1;
}

.nav-link {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 7px 12px;
  border-radius: var(--radius-sm);
  transition: background 0.15s, color 0.15s;
}

.nav-link:hover {
  background: var(--bg-hover);
  color: var(--primary);
}

.nav-link--active {
  background: rgba(54, 87, 61, 0.1);
  color: var(--primary);
}

/* ---- User ---- */
.nav-user {
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-user-name {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.nav-user-role {
  font-size: 0.72rem;
  color: var(--primary);
  background: rgba(54, 87, 61, 0.08);
  border: 1px solid rgba(54, 87, 61, 0.2);
  border-radius: 999px;
  padding: 2px 7px;
}

.role-switcher-select {
  border: 1px solid var(--border);
  border-radius: 999px;
  background: #fff;
  color: var(--text-primary);
  font-size: 0.72rem;
  padding: 3px 8px;
}

.role-switcher-select option {
  color: var(--text-primary);
}

.nav-role-connect {
  border: 1px solid rgba(54, 87, 61, 0.28);
  border-radius: 999px;
  background: rgba(54, 87, 61, 0.08);
  color: var(--primary);
  cursor: pointer;
  font-size: 0.72rem;
  padding: 3px 8px;
}

.nav-role-connect:hover:not(:disabled) {
  background: rgba(54, 87, 61, 0.14);
}

.nav-role-connect:disabled {
  cursor: wait;
  opacity: 0.68;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.nav-user-loading {
  font-size: 0.85rem;
  color: var(--text-disabled);
}

/* ---- Responsive ---- */
@media (max-width: 600px) {
  .nav-inner {
    padding: 0 var(--space-md);
    gap: var(--space-sm);
  }

  .nav-brand-logo {
    width: 92px;
  }

  .nav-brand-copy {
    display: none;
  }

  .nav-link {
    padding: 6px 8px;
    font-size: 0.85rem;
  }
}
</style>
